import { faCheck, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import { RemitFinancialInfo, Remit } from '@/types/billing';
import Alert from '@/ui/components/Alert';
import Badge from '@/ui/components/Badge';
import ViewInCandid from '@/ui/components/ViewInCandid';
import { candidRemitsUrl } from '@/utils/candidLink';
import { currencyFormatter, currencyInCentsFormatter } from '@/utils/currencies';

import type { Props } from './types';

export const reconcileRemitClaimCheck = (finInfo: RemitFinancialInfo, remit: Remit) => {
  const checkAmount = finInfo.check_amount_in_cents || 0;

  const claims = remit.remit_claims?.filter((claim) => claim.financial_info_id === finInfo.id);

  const totalPaidAmount =
    claims?.reduce((acc, claim) => acc + (claim.paid_amount_in_cents || 0), 0) || 0;

  const difference = checkAmount - totalPaidAmount;

  return {
    reconciled: difference === 0,
    difference: difference
  };
};

const RemitFinancialInfoCard = ({ remit, finInfo }: Props) => {
  const { host } = useContext(AppContext);
  const claims = useMemo(
    () => remit?.remit_claims?.filter((claim) => claim.financial_info_id === finInfo.id),
    [remit, finInfo]
  );
  const { reconciled, difference } = useMemo(
    () => reconcileRemitClaimCheck(finInfo, remit),
    [finInfo, remit]
  );

  const renderFieldMatch = (field: string, value: string, match: boolean) => {
    return (
      <span>
        {field}: {value}{' '}
        {match ? (
          <Badge color={Color.RED}>
            <>{} Match</>
          </Badge>
        ) : null}
      </span>
    );
  };

  const renderField = (field: string, value: string) => {
    return (
      <span>
        <span className="text-gray-700 text-sm">{field}</span>: <strong>{value}</strong>
      </span>
    );
  };

  return (
    <div>
      <div className="grid grid-cols-5 relative">
        {renderField('Check #', finInfo?.check_or_eft_trace_number || '-')}
        {/* view in candid */}
        <ViewInCandid
          url={candidRemitsUrl(host, finInfo?.check_or_eft_trace_number || '')}
          className="text-blue-500 ml-2"
        />
        <div className="flex items-center">
          <span className="mr-2">
            {renderField(
              'Amount',
              finInfo?.check_amount ? currencyFormatter(Number(finInfo?.check_amount)) : '-'
            )}

            <span className="ml-2">
              {reconciled ? (
                <FontAwesomeIcon icon={faCheck} className="text-green-500" />
              ) : (
                <span className="text-red-500">
                  <FontAwesomeIcon icon={faExclamationCircle} className="text-red-500 mr-1" />
                  difference: {currencyInCentsFormatter(difference)}
                </span>
              )}
            </span>
          </span>
        </div>
        {renderField('Claims', String((claims || []).length))}
        {renderField('Paid On', finInfo?.paid_at ? new Date(finInfo?.paid_at).toDateString() : '-')}

        {renderField('Payment Method', finInfo?.payment_method || '-')}
      </div>
      {finInfo?.potential_duplicates && finInfo?.potential_duplicates?.length > 0 ? (
        <Alert level="warn" dismissable={false}>
          <strong>Possible Duplicates Found</strong>
          <div className="flex flex-row gap-4">
            {(finInfo?.potential_duplicates || []).map((duplicate) => {
              const matchedCheckAmount = duplicate?.amount_difference == 0;
              const matchedPaidAt = duplicate?.paid_at === finInfo?.paid_at;
              const matchedCheckNumber =
                duplicate?.check_or_eft_trace_number === finInfo?.check_or_eft_trace_number;

              return (
                <div key={`${finInfo?.id}-duplicates`} className="flex flex-row gap-4">
                  {renderFieldMatch(
                    'Amount',
                    duplicate?.check_amount
                      ? currencyFormatter(Number(duplicate?.check_amount))
                      : '-',
                    matchedCheckAmount
                  )}

                  {renderFieldMatch(
                    'Check #',
                    `${duplicate?.check_or_eft_trace_number || '-'}`,
                    matchedCheckNumber
                  )}

                  {renderFieldMatch(
                    'Paid On',
                    duplicate?.paid_at ? new Date(duplicate?.paid_at).toDateString() : '-',
                    matchedPaidAt
                  )}

                  {/* if the remit is different add a link */}
                  {duplicate?.remit_id !== finInfo?.remit_id ? (
                    <Link
                      className="text-blue-500"
                      to={`/billing/remit/${duplicate?.remit_id}/edit`}
                      target="_blank">
                      View Remit
                    </Link>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Alert>
      ) : null}
    </div>
  );
};
export default React.memo(RemitFinancialInfoCard);
